import { FeatureFlagDefaults, FeatureFlags } from '@fullstory/client-api';

import { buildWithCompressedResourceMap, decompressResourceMap } from './resource_mapping';

export type ClientFlags = { [flag: number]: number };

export type ResourceMapEntry = {
  to: string;
  destination?: RequestDestination;
};

// resourceHrefPattern can either be an href or it can be a pattern
// it can contain wildcard (*) in the string
export type ResourceMap = { [resourceHrefPattern: string]: ResourceMapEntry };

/**
 * A compressed resource map. The format is explicit to allow us to easily
 * transition to better compression algorithms and encodings as they become
 * available.
 */
export type CompressedResourceMap =
  // The trivial format: just regular JSON.
  | ['json', ResourceMap]
  // JSON compressed with the deflate algorithm and encoded as base64url.
  | ['json+deflate', string];

/** Metadata that identifies a specific renderer iframe.  */
export type PlaybackInstance = {
  /** The version of the script which created this playback instance. */
  scriptVersion: string;

  /**
   * An arbitary identifier for this particular playback instance. This allows
   * all documents associated with this instance to be tied together in the
   * service worker.
   */
  playbackId: string;
};

/** Metadata about the environment in which playback is taking place. */
type PlaybackEnvironment = {
  /** Active feature flags for this playback instance. */
  flags: {
    client: ClientFlags;
    features: FeatureFlags;
  };
  /** The maximum ms to wait for resources to load before signaling playback is ready. */
  maxResourceWait?: number;
};

/**
 * Metadata for the page loaded in a playback instance. These values mostly
 * either come from the page's {@link PageDesc} directly, or are computed from
 * values on {@link PageDesc}.
 */
export type PageContext = {
  orgId: string;
  sessionId: string;
  userId: string;
  pageId: string;

  /** The type of page - mobile, web, etc. (See {@link PageDesc.Type}.) */
  pageType: string;

  /** The resource proxy to use to fetch resources. */
  resourceProxyPrefix: string;

  /** The GCS bucket which contains snapshotted resources. */
  gcsBucketPrefix: string;

  /**
   * A mapping from resource URLs in the original page to their snapshotted
   * versions. The playback service worker uses this to redirect fetches
   * appropriately during playback.
   */
  resourceMap: ResourceMap | null;

  /** Text corpus used for text replacement during playback. */
  textCorpus?: Record<string, string>;

  windowWidth: number;
  windowHeight: number;
  screenWidth: number;
  screenHeight: number;
};

type PlaybackPage = { page: PageContext };

/**
 * Metadata that describes an instance of the renderer iframe, its environment,
 * and the page loaded in it.
 */
export type PlaybackContext = PlaybackEnvironment & PlaybackInstance & PlaybackPage;

/**
 * The "on the wire" representation of a PageContext. This includes the same
 * data as the PageContext does, but possibly in a different, more efficient
 * encoding.
 *
 * TODO(sethf): It would be cleaner if the 'resourceMap' property did not appear
 * in this type, but it needs to be there for backwards compatibility with older
 * renderer iframes in the short term. Clean this up after April 2021.
 */
type WirePageContext = PageContext & {
  compressedResourceMap: CompressedResourceMap | null;
};

type WirePlaybackPage = { page: WirePageContext };

/** The "on the wire" representation of a PlaybackContext. */
type WirePlaybackContext = PlaybackEnvironment & PlaybackInstance & WirePlaybackPage;

/**
 * @returns a {@link WirePlaybackContext} extracted from the components of 'url'
 * and returns it. If the necessary information isn't there, returns undefined.
 */
function wirePlaybackContextFromUrl(url: URL): WirePlaybackContext | undefined {
  try {
    const maybeCtx = decodeURIComponent(url.hash.substring(1));

    const ctx = JSON.parse(maybeCtx) as WirePlaybackContext;
    if (
      typeof ctx.scriptVersion !== 'string' ||
      typeof ctx.playbackId !== 'string' ||
      typeof ctx.flags !== 'object' ||
      typeof ctx.page !== 'object' ||
      typeof (ctx.maxResourceWait ?? 0) !== 'number' // undefined is valid for maxResourceWait
    ) {
      console.warn(`Playback context looks invalid: ${url.href}`);
      return undefined;
    }

    // Treat 0 as undefined so the value gets defaulted
    ctx.maxResourceWait = ctx.maxResourceWait || undefined;

    // Set default values for any LaunchDarkly flags that the app didn't
    // provide. This can happen in local development when testing a feature flag
    // that hasn't been enabled yet in LaunchDarkly.
    for (const [flagName, defaultValue] of Object.entries(FeatureFlagDefaults)) {
      if (!(flagName in ctx.flags.features)) {
        (ctx.flags.features as any)[flagName] = defaultValue;
      }
    }

    return ctx;
  } catch (e) {
    console.warn(`Playback context did not parse: ${e} - ${url.href}`);
    return undefined;
  }
}

/**
 * @returns a random value suitable for use as a {@link PlaybackContext.playbackId}.
 * TODO(jordanhurt) - we should be able to replace this with @fullstory/util/uid,
 * but I need to make sure that the characters returned are compatible with the ways
 * that randomPlaybackId() is used.
 */
function randomPlaybackId(): string {
  return Math.random().toString(36).substring(2);
}

export function urlWithPlaybackInstance(pageUrl: URL, instance: PlaybackInstance): URL {
  const url = new URL(pageUrl.href);

  url.searchParams.set('playbackId', instance.playbackId);
  url.searchParams.set('scriptVersion', instance.scriptVersion);

  return url;
}

/**
 * @returns a new URL with the same href as the given URL, but with additional
 * query parameters and a new fragment identifier containing the given
 * {@link PlaybackContext} metadata.
 *
 * The purely mechanical properties from {@link PlaybackInstance} are filled in
 * automatically if not specified. This is generally what you want except in a
 * testing context.
 */
function urlWithWirePlaybackContext(
  pageUrl: URL,
  ctx: PlaybackEnvironment & Partial<PlaybackInstance> & PlaybackPage,
  compressedResourceMap: CompressedResourceMap | null,
): URL {
  const url = new URL(pageUrl.href);
  // CompiledTimestamp is not available in all environments, like Storybook
  const scriptVersion = typeof CompiledTimestamp !== 'undefined' ? `${CompiledTimestamp}` : 'N/A';
  const completeCtx = {
    scriptVersion,
    playbackId: randomPlaybackId(),
    ...ctx,
    page: {
      ...ctx.page,
      resourceMap: !compressedResourceMap ? ctx.page.resourceMap : null,
      compressedResourceMap,
    },
  };

  // Note that we encode the context before assigning it to URL#hash. Although
  // URL#hash will apply percent encoding automatically, it does not re-encode
  // content that already appears to be encoded. This means that, for example,
  // URLs in the resource map that include percent-encoded characters will not
  // be doubly percent-encoded, and as a result decoding them in
  // playbackContextFromUrl() will remove all percent-encoding and produce URLs
  // that are invalid or even JSON that doesn't parse.
  url.hash = encodeURIComponent(JSON.stringify(completeCtx));

  // Since all context information lives in the fragment identifier, and the
  // browser cache ignores the fragment identifier when matching entries, the
  // context itself does not provide any cache busting. We duplicate some of the
  // context information as query parameters to ensure that the cache gets
  // busted on script version changes.
  // TODO(sethf): The 'playbackId' query parameter here busts the cache every
  // time a new page gets played back, which is usually overkill, but it's worth
  // it in the short term because the URL format change will totally
  // break playback if the cache fails to bust and an old version of
  // 'fsrenderer.html' gets loaded. Consider removing the 'playbackId' query
  // parameter after the end of April 2020.
  url.searchParams.set('scriptVersion', completeCtx.scriptVersion);
  url.searchParams.set('playbackId', completeCtx.playbackId);

  return url;
}

/**
 * @returns a {@link PlaybackContext} extracted from the components of 'url'.
 * If the necessary information isn't there, returns undefined.
 *
 * Note that this can be computationally expensive in some cases, depending on
 * the underlying encoding of the data in the URL. Consider caching the result
 * or calling {@link playbackInstanceFromUrl} instead if it would meet your
 * needs.
 */
export async function playbackContextFromUrl(url: URL): Promise<PlaybackContext | undefined> {
  const wireCtx = wirePlaybackContextFromUrl(url);
  if (!wireCtx) {
    return undefined;
  }

  // Decompress the resource map, if necessary, and discard the compressed
  // version since it's not part of the public API.
  if (!wireCtx.page.resourceMap) {
    wireCtx.page.resourceMap = await decompressResourceMap(wireCtx.page.compressedResourceMap);
  }
  const ctx: PlaybackContext = wireCtx;
  delete (ctx as any).page.compressedResourceMap;

  return ctx;
}

/**
 * @returns a {@link PlaybackInstance} extracted from the components of 'url'.
 * If the necessary information isn't there, returns undefined.
 *
 * This function returns a subset of the information in the full
 * {@link PlaybackContext}. It can be substantially less expensive than
 * {@link playbackContextFromUrl}, so prefer this function if it meets your
 * needs.
 */
export function playbackInstanceFromUrl(url: URL): PlaybackInstance | undefined {
  try {
    // It's more costly to JSON.parse the entire context object,
    // so only read it if the query params are missing.
    if (!url.searchParams.has('playbackId') || !url.searchParams.has('scriptVersion')) {
      const wireCtx = wirePlaybackContextFromUrl(url);
      return wireCtx
        ? {
            playbackId: wireCtx.playbackId,
            scriptVersion: wireCtx.scriptVersion,
          }
        : undefined;
    }

    const playbackId = url.searchParams.get('playbackId');
    const scriptVersion = url.searchParams.get('scriptVersion');

    if (typeof playbackId !== 'string' || typeof scriptVersion !== 'string') {
      console.warn(`Playback context looks invalid: ${url.href}`);
      return undefined;
    }

    return {
      playbackId,
      scriptVersion,
    };
  } catch (e) {
    return undefined;
  }
}

/**
 * The maximum URL length we can safely use. This value has been experimentally
 * validated as being the maximum URL size that will work in all supported
 * playback browsers, as of February 2021. (Specifically, Blink allows ~2MB,
 * Gecko allows ~1MB, and WebKit has a limit which is higher than both.) A small
 * amount of slack is subtracted from the actual limit to give us headroom in
 * case the URL gets mutated in code that isn't aware of the limit.
 *
 * It's important to be aware that browsers do not apply a single size limit to
 * all URLs consistently. Testing shows that data URLs are allowed to be larger
 * than other URL schemes on every browser. It's important to take this account
 * when writing tests that involve URL size limits.
 */
export const URL_SIZE_LIMIT_BYTES = 1 * 1024 * 1024 - 1024;

/**
 * @returns a new URL with the same href as the given URL, but with additional
 * query parameters and a new fragment identifier containing the given
 * {@link PlaybackContext} metadata.
 *
 * In cases where the resulting URL would exceed browser URL size limits, the
 * {@link ResourceMap} will be compressed to bring the URL size down. This
 * compression process may be lossy, and it may be computationally expensive;
 * the behavior will depend on the content of the resource map and the available
 * browser APIs.
 *
 * The purely mechanical properties from {@link PlaybackInstance} are filled in
 * automatically if not specified. This is generally what you want except in a
 * testing context.
 *
 * Callers can optionally specify a custom URL size limit, but using the default
 * is recommended except in a testing context.
 */
export async function urlWithPlaybackContext(
  pageUrl: URL,
  ctx: PlaybackEnvironment & Partial<PlaybackInstance> & PlaybackPage,
  urlSizeLimitBytes: number = URL_SIZE_LIMIT_BYTES,
): Promise<URL> {
  if (!ctx.flags.features.AppPlaybackResourceMapShrinking) {
    return urlWithWirePlaybackContext(pageUrl, ctx, null);
  }

  const url = await buildWithCompressedResourceMap(
    ctx.page.resourceMap,
    (compressedMap: CompressedResourceMap | null, numDroppedEntries: number) => {
      const url = urlWithWirePlaybackContext(pageUrl, ctx, compressedMap);

      // If the URL exceeds the size limit, return null to indicate that further
      // compression is required.
      const urlLength = url.href.length;
      if (urlLength > urlSizeLimitBytes) {
        return null;
      }

      // We're done; this URL will work. Warn if we had to drop anything.
      if (numDroppedEntries > 0) {
        console.warn(
          `Resource map was too large to fit in the playback URL; dropped ` +
            `${numDroppedEntries} entries. This may cause playback fidelity ` +
            `issues.`,
        );
      }

      return url;
    },
  );

  if (url) {
    return url;
  }

  // Compression continues above until we discard the entire resource map. If the
  // URL was still too big after that, the other stuff in the PlaybackContext
  // must be the culprit. We never expect to take this path, since the resource
  // map is the only thing in the PlaybackContext that can be arbitrarily large.
  throw new Error(
    `Failed to build a PlaybackContext URL with size under the limit ` +
      `of ${urlSizeLimitBytes}; too much non-resource-map data in the ` +
      `PlaybackContext.`,
  );
}
